<template>
    <div>
        <div id="exportable-content">
            <Hero title="Build integrations with your coding agent" :highlightWords="['coding', 'agent']"
                :subtitle="`Point Claude Code, Cursor, Codex, or Copilot at Unified.to. Your agent adds ${INTEGRATIONS_COUNT_10s}+ integrations to your app, manages your workspace, and debugs its own work.`"
                :buttons="[
                    { link: 'https://app.unified.to', title: 'Get started free' },
                    { link: 'https://docs.unified.to/skills/unified-quickstart/SKILL.md', title: 'Read the skill', icon: 'ic:round-keyboard-arrow-right' },
                ]" />

            <section class="max-w-screen-md mx-auto w-full px-6 pb-12">
                <p class="text-neutral-50 text-center mb-4">Paste this into your coding agent:</p>
                <div class="flex items-center gap-4 bg-gray-800 shadow-lg rounded-lg px-4 py-3">
                    <code class="flex-1 min-w-0 break-words font-mono text-sm text-green-300">{{ PROMPT }}</code>
                    <button @click="copyPrompt" title="Copy prompt" class="flex-none text-gray-50">
                        <Icon :name="promptCopied ? 'material-symbols:check-small-rounded' : 'material-symbols:content-copy-outline-rounded'"
                            class="text-lg hover:text-green-500" />
                    </button>
                </div>
            </section>

            <!-- Core MCP -->
            <section class="max-w-screen-xl mx-auto w-full py-12 px-6">
                <div class="grid md:grid-cols-2 gap-16 items-start">
                    <div class="space-y-6 min-w-0">
                        <h2 class="text-4xl font-bold text-white">Connect the Unified.to Core MCP server</h2>
                        <p class="text-neutral-50">
                            Give your agent direct access to your Unified.to workspace. It can check which
                            integrations support what you need, create connections and webhooks, answer questions
                            from the docs, and read API-call logs to fix a failing integration on its own.
                        </p>
                        <ul class="text-gray-400 space-y-2">
                            <li>&middot; Streamable HTTP endpoints in the US, EU, and AU</li>
                            <li>&middot; Authenticated with your workspace API key</li>
                            <li>&middot; Works on workspace configuration only &mdash; never your customers' data</li>
                        </ul>
                        <a href="https://docs.unified.to/mcp/core" class="text-blue-500 flex items-center gap-2">
                            Core MCP documentation
                            <Icon name="ic:round-keyboard-arrow-right" />
                        </a>
                    </div>
                    <div class="min-w-0">
                        <AgentMcpCode />
                    </div>
                </div>
            </section>

            <!-- What the agent can do -->
            <section class="max-w-screen-xl mx-auto w-full py-12 px-6">
                <div class="mb-12 text-center">
                    <h2 class="text-4xl font-bold text-white mb-6">What your agent can do</h2>
                    <p class="text-neutral-50 text-xl max-w-3xl mx-auto">
                        Ask in plain language. The Core MCP server turns it into workspace actions.
                    </p>
                </div>
                <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div v-for="capability of CAPABILITIES" :key="capability.title"
                        class="rounded-lg border border-gray-700 p-6">
                        <h3 class="text-lg font-semibold text-white mb-2">{{ capability.title }}</h3>
                        <p class="text-gray-400 mb-4">{{ capability.body }}</p>
                        <p class="text-sm font-mono text-green-300">&ldquo;{{ capability.prompt }}&rdquo;</p>
                    </div>
                </div>
            </section>

            <!-- How it works -->
            <section class="max-w-screen-lg mx-auto w-full py-12 px-6">
                <div class="mb-12 text-center">
                    <h2 class="text-4xl font-bold text-white mb-6">From prompt to working integration</h2>
                    <p class="text-neutral-50 text-xl max-w-3xl mx-auto">
                        The quick-start skill walks your agent through the same steps as our
                        <a href="https://docs.unified.to/quick-start" class="text-blue-500">quick start</a>, in your
                        codebase.
                    </p>
                </div>
                <ol class="space-y-8">
                    <li v-for="(step, index) of STEPS" :key="step.title" class="flex gap-6">
                        <span
                            class="flex-none h-10 w-10 rounded-full border border-[var(--primary-blue)] text-[var(--primary-blue)] flex items-center justify-center font-semibold">
                            {{ index + 1 }}
                        </span>
                        <div>
                            <h3 class="text-lg font-semibold text-white">{{ step.title }}</h3>
                            <p class="text-gray-400">{{ step.body }}</p>
                        </div>
                    </li>
                </ol>
                <p class="text-gray-400 mt-10">
                    <strong class="text-white">What you do:</strong> create your workspace and copy your API keys,
                    activate integrations, review the agent's plan and changes, and when you launch, register your
                    app with each vendor and switch to Production.
                </p>
            </section>

            <!-- Category skills -->
            <section class="max-w-screen-xl mx-auto w-full py-12 px-6">
                <div class="mb-12 text-center">
                    <h2 class="text-4xl font-bold text-white mb-6">Skills for every category</h2>
                    <p class="text-neutral-50 text-xl max-w-3xl mx-auto">
                        After the quick start, your agent loads the skill for your use case: object shapes,
                        sync patterns, and category-specific rules.
                    </p>
                </div>
                <div class="max-w-screen-md mx-auto mb-12">
                    <p class="text-neutral-50 text-center mb-4">
                        Install them all in Codex, Cursor, Copilot, Windsurf, and other agents (Claude Code users: use the
                        plugin above):
                    </p>
                    <CopyCode :code="SKILLS_INSTALL_CODE" label="Terminal" page="Agents Skills" />
                </div>
                <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <a v-for="skill of SKILLS" :key="skill.name"
                        :href="`https://docs.unified.to/skills/${skill.name}/SKILL.md`"
                        class="rounded-lg border border-gray-700 p-4 hover:border-[var(--primary-blue)]">
                        <span class="block text-white font-semibold">{{ skill.title }}</span>
                        <span class="block text-xs font-mono text-gray-400 break-words">{{ skill.name }}</span>
                    </a>
                </div>
            </section>

            <!-- CLI -->
            <section class="max-w-screen-xl mx-auto w-full py-12 px-6">
                <div class="grid md:grid-cols-2 gap-16 items-center">
                    <div class="space-y-6 min-w-0">
                        <h2 class="text-4xl font-bold text-white">Prefer the terminal?</h2>
                        <p class="text-neutral-50">
                            The Unified.to CLI gives agents and developers CRUD on any object, passthrough calls
                            to the vendor API, and <code>unified objects</code> to discover every object, method,
                            and list filter.
                        </p>
                        <a href="https://github.com/unified-to/unified-cli" class="text-blue-500 flex items-center gap-2">
                            Unified.to CLI on GitHub
                            <Icon name="ic:round-keyboard-arrow-right" />
                        </a>
                    </div>
                    <div class="min-w-0">
                        <CopyCode :code="CLI_CODE" label="Terminal" page="Agents CLI" />
                    </div>
                </div>
            </section>

            <!-- Core MCP vs Unified MCP -->
            <section class="max-w-screen-lg mx-auto w-full py-12 px-6">
                <h2 class="text-4xl font-bold text-white mb-10 text-center">Two MCP servers, two jobs</h2>
                <div class="grid md:grid-cols-2 gap-6">
                    <div class="rounded-lg border border-gray-700 p-6">
                        <h3 class="text-lg font-semibold text-white mb-2">Core MCP &mdash; for your coding agent</h3>
                        <p class="text-gray-400">
                            Builds and operates your integrations: workspace integrations, connections, webhooks,
                            environments, logs, and docs search.
                        </p>
                    </div>
                    <a href="/mcp" class="rounded-lg border border-gray-700 p-6 hover:border-[var(--primary-blue)]">
                        <h3 class="text-lg font-semibold text-white mb-2">Unified MCP &mdash; for your product's
                            agents</h3>
                        <p class="text-gray-400">
                            Gives the AI features you ship real-time, authorized access to your customers' SaaS
                            data as callable tools.
                        </p>
                    </a>
                </div>
            </section>

            <!-- FAQ -->
            <section class="max-w-screen-lg mx-auto w-full py-12 px-6">
                <a class="anchor" name="faq" id="faq"></a>
                <h2 class="text-2xl font-bold text-white border-b border-gray-700 pb-4">
                    Frequently Asked Questions
                </h2>
                <Accordion v-model="activeFaq" :labels="AGENTS_FAQS.map(faq => faq.title)" class="mt-8">
                    <template v-for="(faq, index) of AGENTS_FAQS" :key="faq.id" v-slot:[faqSlot(index)]>
                        <a class="anchor" :name="faq.id" :id="faq.id" />
                        <span v-html="faq.answer.replace(/\n/g, '<br>')" class="text-gray-400" />
                    </template>
                </Accordion>
            </section>

            <Hero title="Ship integrations. Faster." :highlightWords="['faster.']" headingLevel="h2" bg :buttons="[
                { link: 'https://app.unified.to', title: 'Get started free' },
                { link: '/contact', title: 'Speak to an expert', icon: 'ic:round-keyboard-arrow-right' },
            ]" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { INTEGRATIONS_COUNT_10s } from '~/services/integrations';
import { AGENTS_FAQS } from '~/constants/agents-faq';

const PROMPT = 'Add Unified.to integrations to my app: https://unified.to/SKILL.md';

const SKILLS_INSTALL_CODE = 'npx skills add https://github.com/unified-to/unified-docs/tree/main/public/skills';
const CLI_CODE = `brew tap unified-to/cli && brew install unified
export UNIFIED_API_KEY=...

unified objects crm
unified list <connection_id> crm_contact --limit 10`;

const CAPABILITIES = [
    {
        title: 'Search the docs',
        body: 'Answers API questions from docs.unified.to instead of guessing.',
        prompt: 'How does pagination work for HRIS employees?',
    },
    {
        title: 'Check integration support',
        body: 'Lists the integrations and features available to your workspace.',
        prompt: 'Which CRMs support deal webhooks?',
    },
    {
        title: 'Manage connections',
        body: 'Creates, inspects, and updates connections, and generates scoped MCP URLs.',
        prompt: 'Show me the connections for user 42.',
    },
    {
        title: 'Set up webhooks',
        body: 'Registers and updates webhook subscriptions for real-time sync.',
        prompt: 'Send updated CRM contacts to /api/webhooks/unified.',
    },
    {
        title: 'Debug failures',
        body: 'Reads API-call logs and workspace issues to find the root cause.',
        prompt: "Why did yesterday's Salesforce sync fail?",
    },
    {
        title: 'Manage environments',
        body: 'Creates and lists environments so development stays out of production.',
        prompt: 'Create a staging environment.',
    },
];

const STEPS = [
    {
        title: 'Plan',
        body: 'The agent inspects your project, confirms the categories, integrations, and region you need, and waits for your approval before changing code.',
    },
    {
        title: 'Activate in the Sandbox',
        body: 'Integrations are activated in the Sandbox, which returns synthetic data and accepts mock credentials — no vendor developer accounts needed yet.',
    },
    {
        title: 'Add authorization and the first API call',
        body: 'The agent installs the SDK, embeds the Authorization component, adds a callback route that verifies the signature and stores the connection ID, and makes the first call.',
    },
    {
        title: 'Verify and sync',
        body: 'It tests the flow end to end, checks the API-call logs, and optionally registers webhooks with signature verification for real-time sync.',
    },
];

const SKILLS = [
    { title: 'CRM', name: 'unified-crm' },
    { title: 'ATS & job boards', name: 'unified-ats-jobboard' },
    { title: 'Assessments', name: 'unified-assessment' },
    { title: 'HRIS & payroll', name: 'unified-hris' },
    { title: 'Accounting', name: 'unified-accounting-invoicing' },
    { title: 'Payments', name: 'unified-payments' },
    { title: 'E-commerce', name: 'unified-ecommerce' },
    { title: 'Messaging', name: 'unified-messaging' },
    { title: 'Calendar', name: 'unified-calendar' },
    { title: 'Ticketing', name: 'unified-ticketing' },
    { title: 'Enterprise search & RAG', name: 'unified-enterprise-search-rag' },
    { title: 'Sign in with…', name: 'unified-auth-signin' },
    { title: 'REST API', name: 'unified-api' },
    { title: 'SDKs', name: 'unified-sdks' },
    { title: 'Debugging', name: 'unified-debug-webhooks-connections' },
];

const promptCopied = ref(false);
function copyPrompt() {
    navigator?.clipboard?.writeText(PROMPT).then(() => {
        promptCopied.value = true;
        setTimeout(() => (promptCopied.value = false), 2000);
    });
}

const activeFaq = ref(0);
const faqSlot = (index: number) => `section-${index + 1}`;

useSEO({
    title: 'Build with Agents',
    description: `Add ${INTEGRATIONS_COUNT_10s}+ SaaS integrations to your app with Claude Code, Cursor, Codex, or Copilot. A quick-start skill plus the Unified.to Core MCP server let coding agents build, configure, and debug integrations.`,
    jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: AGENTS_FAQS.map((faq) => ({
            '@type': 'Question',
            name: faq.title,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer.replace(/<[^>]*>/g, '').trim(),
            },
        })),
    },
});
</script>
